import {
  faArchive,
  faChartKanban,
  faCheck,
  faExclamationTriangle,
  faGavel,
  faShuffle,
  faUser,
  faXmark
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import type { ReactElement, ReactNode } from 'react'
import { useCallback, useMemo, useState } from 'react'
import DataTable, {
  DataTableFilterableCell,
  type DataTableQueryParams,
  DataTableRowAction,
  type DataTableRowSelection,
  defineColumns
} from '~/components/DataTable/DataTable'
import {
  cellContentMain,
  cellContentRowMain,
  cellContentSub,
  cellIconMuted,
  clickableCell,
  dataInlineCell
} from '~/components/DataTable/DataTable.css'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { ApplicantModal } from '~/components/Scopes/Applicants/ApplicantModal/ApplicantModal'
import { useJobboards } from '~/domains/applications/queries/useJobboards'
import type { ApplicationCollection } from '~/domains/applications/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { UsersAutocomplete } from '~/domains/users/components/UsersAutocomplete'
import { UsersList } from '~/domains/users/components/UsersList'
import { vars } from '~/styles/theme.css'
import { formatDateTime, toISOLocal } from '~/utils/date'
import { formatName } from '~/utils/names'

type ApplicationsTableProps = {
  data: ApplicationCollection[]
  totalItems: number
  queryParams: DataTableQueryParams
  onQueryChange: (params: DataTableQueryParams) => void
  onResetFilters?: () => void
  showResetFilters?: boolean
  rowSelection?: DataTableRowSelection
  onRowSelectionChange?: (selection: DataTableRowSelection) => void
  onOpenApplication: (application: ApplicationCollection) => void
  bulkActions?: (selectedIds: string[]) => ReactNode
  isLoading?: boolean
  isFetching?: boolean
  onRefresh?: () => void
}

export const ApplicationsTable = ({
  data,
  totalItems,
  queryParams,
  onQueryChange,
  onResetFilters,
  showResetFilters,
  rowSelection,
  onRowSelectionChange,
  onOpenApplication,
  bulkActions,
  isLoading,
  isFetching,
  onRefresh
}: ApplicationsTableProps): ReactElement => {
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const { data: jobboards = [] } = useJobboards()
  const [applicantId, setApplicantId] = useState<number | null>(null)

  const renderRowActions = useCallback(
    (row: ApplicationCollection) => (
      <>
        <DataTableRowAction
          icon={faUser}
          label="Fiche candidat"
          disabled={!row.applicant}
          disabledReason="Aucun candidat associé"
          onClick={() => {
            if (row.applicant) setApplicantId(row.applicant.id)
          }}
        />
        <DataTableRowAction
          icon={faShuffle}
          label="Offre liée (nouvelle fenêtre)"
          disabled={!row.offer}
          disabledReason="Aucune offre associée"
          href={
            row.offer && legacyUrl
              ? `${legacyUrl}/admin/offres/voir/${row.offer.id}`
              : undefined
          }
          target="_blank"
          rel="noreferrer"
        />
        <DataTableRowAction
          icon={faChartKanban}
          label="Commande liée"
          disabled={!row.order}
          disabledReason="Aucune commande associée"
          link={
            row.order
              ? {
                  to: '/orders/$orderId',
                  params: { orderId: String(row.order.id) }
                }
              : undefined
          }
        />
        <DataTableRowAction
          icon={faGavel}
          label="Traiter la candidature"
          onClick={() => onOpenApplication(row)}
        />
      </>
    ),
    [legacyUrl, onOpenApplication]
  )

  const columns = useMemo(
    () =>
      defineColumns<ApplicationCollection>([
        {
          id: 'sender',
          header: "Nom de l'expéditeur",
          defaultSize: 350,
          meta: {
            sticky: true,
            filterPlaceholder: 'Rechercher...'
          },
          cell: ({ row }) => {
            const applicant = row.applicant
            return (
              <div className={dataInlineCell}>
                <span
                  title={applicant ? 'Fiche candidat' : 'Pas dans le vivier'}
                  className={applicant ? clickableCell : cellIconMuted}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (applicant) setApplicantId(applicant.id)
                  }}
                >
                  <FontAwesomeIcon icon={faUser} size="lg" />
                </span>
                {row.isSpam ? (
                  <Tooltip
                    text="Candidature marquée comme indésirable"
                    direction="top"
                  >
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      style={{ color: vars.color.danger }}
                      size="lg"
                    />
                  </Tooltip>
                ) : null}
                {row.isArchived ? (
                  <Tooltip text="Candidature archivée" direction="top">
                    <FontAwesomeIcon
                      icon={faArchive}
                      style={{ color: vars.color.warning }}
                      size="lg"
                    />
                  </Tooltip>
                ) : null}
                <span
                  className={clickableCell}
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenApplication(row)
                  }}
                >
                  <span className={clsx(cellContentRowMain, cellContentMain)}>
                    {[row.firstname, row.lastname].filter(Boolean).join(' ') ||
                      '—'}
                  </span>
                  {row.email && (
                    <>
                      <br />
                      <span className={cellContentSub}>{row.email}</span>
                    </>
                  )}
                </span>
              </div>
            )
          },
          enableSorting: true,
          enableColumnFilter: true
        },
        {
          id: 'isCompleted',
          header: 'Traitée',
          defaultSize: 140,
          accessor: 'isCompleted',
          enableSorting: false,
          meta: {
            filterType: 'boolean',
            filterBooleanText: {
              trueOption: 'Traitées',
              falseOption: 'Non traitées',
              emptyOption: 'Toutes'
            }
          },
          enableColumnFilter: true,
          cell: ({ row, value, filterByValue }) => {
            const completed = (value as boolean) || row.isArchived
            return (
              <DataTableFilterableCell
                filterValue={completed ? 'true' : 'false'}
                onFilter={filterByValue}
              >
                {completed ? (
                  <Badge
                    title="Traitée"
                    icon={faCheck}
                    backgroundColor="#15C590"
                  />
                ) : (
                  <Badge
                    title="Non traitée"
                    icon={faXmark}
                    backgroundColor="#D0DADD"
                  />
                )}
              </DataTableFilterableCell>
            )
          }
        },
        {
          id: 'offer',
          header: "Offre d'emploi",
          defaultSize: 300,
          accessor: (row) => row.offer?.title ?? null,
          enableSorting: true,
          enableColumnFilter: true,
          meta: { filterPlaceholder: 'Rechercher...' },
          cell: ({ row }) => {
            const offer = row.offer
            if (!offer) return 'Candidature spontanée'
            if (legacyUrl) {
              return (
                <a
                  href={`${legacyUrl}/admin/offres/voir/${offer.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {offer.title}
                </a>
              )
            }
            return <span>{offer.title ?? '—'}</span>
          }
        },
        {
          id: 'company',
          header: 'Entreprise',
          defaultSize: 250,
          accessor: (row) => row.order?.company?.name ?? null,
          enableSorting: true,
          enableColumnFilter: true,
          meta: { filterPlaceholder: 'Rechercher...' },
          cell: ({ row }) => {
            const order = row.order
            return (
              <>
                {order?.company ? (
                  <a
                    href={`${legacyUrl}/admin/entreprises/edit/${order.company.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className={cellContentMain}
                  >
                    {order.company.name}
                    {order?.contact && (
                      <>
                        <br />
                        <span className={cellContentSub}>
                          {formatName(order.contact)}
                        </span>
                      </>
                    )}
                  </a>
                ) : (
                  '—'
                )}
              </>
            )
          }
        },
        {
          id: 'jobboard',
          header: 'Jobboard',
          defaultSize: 200,
          accessor: 'jobboardName',
          enableSorting: true,
          enableColumnFilter: true,
          meta: {
            filterType: 'list',
            filterListOptions: jobboards
              .filter((j) => j.name)
              .map((j) => ({ label: j.name, value: j.name }))
          },
          cell: ({ value, filterByValue }) => {
            if (!value) return '—'
            return (
              <DataTableFilterableCell
                filterValue={String(value)}
                onFilter={filterByValue}
              >
                {String(value)}
              </DataTableFilterableCell>
            )
          }
        },
        {
          id: 'receivedAt',
          header: 'Date de réception',
          defaultSize: 180,
          accessor: 'receivedAt',
          enableSorting: true,
          enableColumnFilter: true,
          meta: { filterType: 'date', cellMono: true },
          cell: ({ value, filterByValue }) => {
            if (!value) return '—'
            const date = new Date(String(value))
            const isoDate = toISOLocal(date).slice(0, 10)
            return (
              <DataTableFilterableCell
                filterValue={isoDate}
                onFilter={filterByValue}
              >
                {formatDateTime(date, { dateStyle: 'short' })}
              </DataTableFilterableCell>
            )
          }
        },
        {
          id: 'city',
          header: 'Ville',
          defaultSize: 160,
          accessor: (row) => row.offer?.address?.city ?? null,
          enableSorting: true,
          enableColumnFilter: true,
          meta: { filterPlaceholder: 'Rechercher...' },
          cell: ({ value, filterByValue }) => {
            if (!value) return '—'
            return (
              <DataTableFilterableCell
                filterValue={String(value)}
                onFilter={filterByValue}
              >
                {String(value)}
              </DataTableFilterableCell>
            )
          }
        },
        {
          id: 'user',
          header: 'Utilisateur',
          defaultSize: 160,
          accessor: (row) => row.offer?.user?.lastname ?? null,
          enableSorting: true,
          enableColumnFilter: true,
          meta: {
            filterComponent: (value, onChange) => (
              <UsersAutocomplete
                key={value ?? ''}
                ids={value ? [value] : undefined}
                onChange={(val) => onChange(val || undefined)}
                extraSmall
              />
            )
          },
          cell: ({ row, filterByValue }) => {
            const user = row.offer?.user
            if (!user) return '—'
            return (
              <DataTableFilterableCell
                filterValue={String(user.id)}
                onFilter={filterByValue}
              >
                <UsersList users={[user]} align="left" />
              </DataTableFilterableCell>
            )
          }
        }
      ]),
    [legacyUrl, jobboards, onOpenApplication]
  )

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        totalItems={totalItems}
        queryParams={queryParams}
        defaultSorting={[{ id: 'receivedAt', desc: true }]}
        onQueryChange={onQueryChange}
        onResetFilters={onResetFilters}
        showResetFilters={showResetFilters}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        getRowId={(row) => String(row.id)}
        bulkActions={bulkActions}
        rowActions={renderRowActions}
        isLoading={isLoading}
        isFetching={isFetching}
        onRefresh={onRefresh}
        configName="tables.applications"
      />
      {applicantId && (
        <ApplicantModal
          defaultApplicantId={applicantId}
          collection={[{ id: applicantId }]}
          onClose={() => setApplicantId(null)}
        />
      )}
    </>
  )
}
